import { db, storage } from './firebase-init';
import { ref, deleteObject, listAll } from 'firebase/storage';
import { deleteDoc } from 'firebase/firestore';
import {
  collection, doc, addDoc, updateDoc, getDoc, getDocs, onSnapshot,
  query, where, orderBy, serverTimestamp
} from 'firebase/firestore';

const JOB_ORDERS = 'jobOrders';

export async function createJobOrder(estimateId, estimate, contractNumber, createdBy) {
  const c = estimate.customer || {};
  
  // Use pre-filled windows if provided, otherwise build from estimate lines.
  // Notes are NOT included as window rows - they're pulled out separately into
  // the lineNotes array below. Notes exist for the customer on the estimate;
  // the job order is for internal production and doesn't need them as rows.
  //
  // qboName is preserved here as a safety net so the QBO sync sees a valid
  // mapping even when this fallback path is used (the normal flow goes through
  // CreateJobOrderPage.handleSave which already populates qboName per window).
  const windows = (estimate.windows && estimate.windows.length > 0)
    ? estimate.windows
    : (estimate.lines || [])
        .filter(l => l.type !== 'note')
        .map((line, i) => ({
          lineIndex: i,
          type: line.type,
          lineType: line.type,
          qty: line.qty || 1,
          description: line.description || '',
          width: line.width || '',
          height: line.height || '',
          room: line.room || '',
          unitPrice: line.price || 0,
          style: line.description || '',
          colorInside: '',
          colorOutside: '',
          glass: '',
          flanges: false,
          gridColonial: false,
          gridDiamond: false,
          gridPrairie: false,
          screenHalf: false,
          screenFull: false,
          extrasNotes: '',
          qboName: line.qboName || null,
        }));

  const notes = (estimate.lines || [])
    .filter(l => l.type === 'note')
    .map(l => l.description)
    .filter(Boolean);

  const data = {
    estimateId,
    contractNumber: String(contractNumber).trim(),
    status: 'pending_review', // pending_review → approved → scheduled
    
    // Customer info carried forward
    customer: c,
    locationLabel: c.locationLabel || '',
    
    // Financial carried forward
    subtotal: estimate.subtotal || 0,
    taxEnabled: estimate.taxEnabled || false,
    taxRate: estimate.taxRate || '2.041',
    taxAmount: estimate.taxAmount || 0,
    grandTotal: estimate.grandTotal || 0,
    
    // Line items with technical specs
    windows,
    
    // Notes carried forward
    estimateNotes: estimate.notes || '',
    lineNotes: notes,
    
    // Office manager fields
    officeManagerNotes: '',
    officeManagerApprovedAt: null,
    officeManagerApprovedBy: null,
    
    // Meta
    createdBy: createdBy || '',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    orderDate: estimate.orderDate || '',
  };

  const ref = await addDoc(collection(db, JOB_ORDERS), data);
  return { id: ref.id, ...data };
}

export async function updateJobOrder(id, updates) {
  const ref = doc(db, JOB_ORDERS, id);
  await updateDoc(ref, { ...updates, updatedAt: serverTimestamp() });
}

export async function getJobOrder(id) {
  const snap = await getDoc(doc(db, JOB_ORDERS, id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export function subscribeToJobOrders(statusFilter, callback) {
  const q = statusFilter
    ? query(collection(db, JOB_ORDERS), where('status', '==', statusFilter))
    : query(collection(db, JOB_ORDERS), orderBy('createdAt', 'desc'));
  return onSnapshot(q, snap => {
    const results = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    // Deduplicate by estimateId — keep most recently UPDATED
    const seen = new Map();
    results.forEach(jo => {
      const key = jo.estimateId || jo.id;
      const existing = seen.get(key);
      const joTime = jo.updatedAt?.seconds || jo.createdAt?.seconds || 0;
      const exTime = existing?.updatedAt?.seconds || existing?.createdAt?.seconds || 0;
      if (!existing || joTime > exTime) seen.set(key, jo);
    });
    const deduped = Array.from(seen.values());
    deduped.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
    callback(deduped);
  });
}

export async function getJobOrders(statusFilter = null) {
  let q = statusFilter
    ? query(collection(db, JOB_ORDERS), where('status', '==', statusFilter))
    : query(collection(db, JOB_ORDERS), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  // client-side sort newest first (avoids composite index requirement)
  const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  // Sort client-side when filtered
  if (statusFilter) {
    docs.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
  }
  return docs;
}

export async function getJobOrderByEstimate(estimateId) {
  const q = query(collection(db, JOB_ORDERS), where('estimateId', '==', estimateId));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  // Return the most recently updated one
  docs.sort((a, b) => (b.updatedAt?.seconds || b.createdAt?.seconds || 0) - (a.updatedAt?.seconds || a.createdAt?.seconds || 0));
  return docs[0];
}

export async function getJobOrderByContractNumber(contractNumber) {
  if (!contractNumber) return null;
  const q = query(collection(db, JOB_ORDERS), where('contractNumber', '==', String(contractNumber).trim()));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  // Return the most recently updated one
  docs.sort((a, b) => (b.updatedAt?.seconds || b.createdAt?.seconds || 0) - (a.updatedAt?.seconds || a.createdAt?.seconds || 0));
  return docs[0];
}

export async function getJobOrderByCalendarId(calendarJobId) {
  const q = query(collection(db, JOB_ORDERS), where('calendarJobId', '==', calendarJobId));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  return { id: snap.docs[0].id, ...snap.docs[0].data() };
}

export async function deleteJobOrder(jobOrderId) {
  try {
    // Get job order to find linked jobs
    const snap = await getDoc(doc(db, JOB_ORDERS, jobOrderId));
    if (!snap.exists()) return;
    const jo = snap.data();
    const estimateId = jo.estimateId;

    // Delete linked calendar/TWP job
    if (jo.calendarJobId) {
      try {
        // Also delete linked cutlist job via calendar job
        const calSnap = await getDoc(doc(db, 'jobs', jo.calendarJobId));
        if (calSnap.exists()) {
          const cutlistJobId = calSnap.data().cutlistJobId;
          if (cutlistJobId) {
            await deleteDoc(doc(db, 'cutlistJobs', cutlistJobId));
          }
        }
        await deleteDoc(doc(db, 'jobs', jo.calendarJobId));
      } catch(e) { console.warn('Could not delete calendar job:', e); }
    }

    // Delete linked cutlist job directly if exists
    if (jo.cutlistJobId) {
      try { await deleteDoc(doc(db, 'cutlistJobs', jo.cutlistJobId)); } 
      catch(e) { console.warn('Could not delete cutlist job:', e); }
    }

    // Delete PDFs from Firebase Storage
    const woNum = jo.contractNumber;
    if (woNum && storage) {
      try {
        const folder = ref(storage, `jobs/${woNum}`);
        const list = await listAll(folder);
        await Promise.all(list.items.map(item => deleteObject(item).catch(() => {})));
      } catch(e) { console.warn('Could not delete PDFs:', e); }
    }

    // Delete ALL job order documents for this estimate (handles duplicates)
    if (estimateId) {
      const allQ = query(collection(db, JOB_ORDERS), where('estimateId', '==', estimateId));
      const allSnap = await getDocs(allQ);
      await Promise.all(allSnap.docs.map(d => deleteDoc(d.ref)));
    } else {
      await deleteDoc(doc(db, JOB_ORDERS, jobOrderId));
    }
  } catch(e) {
    console.error('Error deleting job order:', e);
    throw e;
  }
}

export async function approveJobOrder(id, approvedBy) {
  await updateJobOrder(id, {
    status: 'approved',
    officeManagerApprovedAt: serverTimestamp(),
    officeManagerApprovedBy: approvedBy,
  });
}

export const JOB_ORDER_STATUS = {
  pending_review: { label: 'Pending Review', color: '#d97706', bg: '#fef3c7' },
  approved:       { label: 'Approved',        color: '#059669', bg: '#f0fdf4' },
  scheduled:      { label: 'Scheduled',       color: '#2563eb', bg: '#eff6ff' },
  completed:      { label: 'Completed',       color: '#6b7280', bg: '#f3f4f6' },
};

export const GLASS_TYPES = [
  'Clear', 'Low-E / Argon', 'Low-E / Krypton', 'Obscure', 
  'Tempered', 'Tinted', 'Laminated', 'Dual Pane', 'Triple Pane'
];

export const GRID_TYPES = ['None', 'Colonial', 'Diamond', 'Prairie', 'Custom'];
export const SCREEN_TYPES = ['None', 'Half Screen', 'Full Screen', 'FlexScreen'];
export const HINGE_DIRECTIONS = ['N/A', 'Left Hand', 'Right Hand'];
export const FLANGE_OPTIONS = ['Yes', 'No', 'Exterior Only', 'Interior Only'];
export const COLOR_OPTIONS = [
  'White', 'Clay', 'Bronze', 'Black', 'Tan', 
  'Almond', 'Dk Woodgrain/White', 'Dk Woodgrain/Clay'
];

// TWP-manufactured window types (map to cutlist codes)
export const TWP_WINDOW_TYPES = [
  { label: 'Double Hung',      code: 'DH'   },
  { label: 'Horizontal Slider', code: 'HS'  },
  { label: 'Casement',         code: 'CSMT' },
  { label: 'Picture Window',   code: 'PW'   },
  { label: 'Basement Hopper',  code: 'BSMT' },
  { label: '3-Lite Slider',    code: '3HS'  },
  { label: 'Brickmould',       code: 'BM'   },
];
// Non-TWP items (no cutlist)
export const OTHER_ITEM_TYPES = [
  'Entry Door', 'Patio Door', 'Storm Door', 'Interior Door',
  'Trim Out', 'Mileage', 'Labor', 'Other',
];
export const STYLE_OPTIONS = [
  ...TWP_WINDOW_TYPES.map(w => w.label),
  ...OTHER_ITEM_TYPES,
];
